import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiGrid,
  FiFileText,
  FiUsers,
  FiInbox,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiSend,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiEye,
  FiRefreshCw,
  FiLoader,
  FiChevronRight,
  FiAward,
  FiBookOpen,
} from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import "./MyWorkshops.css";

// ========================================
// ANIMATION VARIANTS
// ========================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const tabVariants = {
  inactive: { scale: 1 },
  active: { scale: 1.02 },
};

// ========================================
// SUB-COMPONENTS
// ========================================

// Tab Button Component
const MWPTabButton = ({ icon: Icon, label, count, isActive, onClick }) => (
  <motion.button
    className={`mwp-tab ${isActive ? "mwp-tab-active" : ""}`}
    onClick={onClick}
    variants={tabVariants}
    animate={isActive ? "active" : "inactive"}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <Icon size={18} />
    <span className="mwp-tab-label">{label}</span>
    {count !== undefined && count > 0 && (
      <span className="mwp-tab-count">{count}</span>
    )}
  </motion.button>
);

// Status Filter Pill
const MWPFilterPill = ({ label, count, isActive, onClick, color }) => (
  <motion.button
    className={`mwp-filter-pill ${isActive ? "mwp-filter-active" : ""}`}
    onClick={onClick}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    style={isActive ? { "--pill-color": color } : {}}
  >
    <span>{label}</span>
    {count !== undefined && <span className="mwp-pill-count">{count}</span>}
  </motion.button>
);

// Workshop Card for My Workshops
const MWPWorkshopCard = ({
  workshop,
  type,
  onEdit,
  onPublish,
  onDelete,
  publishing,
}) => {
  const navigate = useNavigate();

  const getStatusBadge = () => {
    if (workshop.status === "draft") {
      return { text: "Draft", class: "mwp-status-draft", icon: FiFileText };
    }
    if (workshop.status === "published") {
      return {
        text: "Published",
        class: "mwp-status-published",
        icon: FiCheckCircle,
      };
    }
    return null;
  };

  const statusBadge = getStatusBadge();

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBA";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <motion.article
      className="mwp-workshop-card"
      variants={itemVariants}
      whileHover={{ y: -4 }}
      onClick={() => navigate(`/workshops/${workshop._id}`)}
    >
      {/* Status Badge */}
      {statusBadge && (
        <span className={`mwp-card-status ${statusBadge.class}`}>
          <statusBadge.icon size={12} />
          {statusBadge.text}
        </span>
      )}

      {/* Card Header */}
      <div className="mwp-card-header">
        {workshop.category && (
          <span className="mwp-card-category">{workshop.category}</span>
        )}
        <span
          className={`mwp-card-price ${
            workshop.pricing?.type === "free" ? "mwp-price-free" : ""
          }`}
        >
          {workshop.pricing?.type === "free"
            ? "Free"
            : `$${workshop.pricing?.amount || 0}`}
        </span>
      </div>

      {/* Title */}
      <h3 className="mwp-card-title">{workshop.title}</h3>

      {/* Description */}
      {workshop.description && (
        <p className="mwp-card-desc">
          {workshop.description.length > 80
            ? workshop.description.substring(0, 80) + "..."
            : workshop.description}
        </p>
      )}

      {/* Meta */}
      <div className="mwp-card-meta">
        <div className="mwp-meta-item">
          <FiCalendar size={14} />
          <span>
            {formatDate(workshop.duration?.startDate)}
            {workshop.duration?.endDate &&
              ` - ${formatDate(workshop.duration?.endDate)}`}
          </span>
        </div>
        <div className="mwp-meta-item">
          <FiUsers size={14} />
          <span>
            {workshop.participantCount || 0}
            {workshop.capacity?.limit && `/${workshop.capacity.limit}`}
          </span>
        </div>
      </div>

      {/* Draft Actions */}
      {type === "drafts" && (
        <div className="mwp-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="mwp-btn mwp-btn-edit"
            onClick={() => onEdit(workshop._id)}
          >
            <FiEdit3 size={14} />
            <span>Edit</span>
          </button>
          <button
            className="mwp-btn mwp-btn-publish"
            onClick={() => onPublish(workshop._id)}
            disabled={publishing === workshop._id}
          >
            {publishing === workshop._id ? (
              <>
                <FiLoader size={14} className="mwp-spin" />
                <span>Publishing...</span>
              </>
            ) : (
              <>
                <FiSend size={14} />
                <span>Publish</span>
              </>
            )}
          </button>
          <button
            className="mwp-btn mwp-btn-delete"
            onClick={() => onDelete(workshop._id)}
          >
            <FiTrash2 size={14} />
          </button>
        </div>
      )}

      {/* Published Actions */}
      {type === "created" && (
        <div className="mwp-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="mwp-btn mwp-btn-manage"
            onClick={() => navigate(`/workshops/${workshop._id}/manage`)}
          >
            <FiGrid size={14} />
            <span>Manage</span>
          </button>
          <button
            className="mwp-btn mwp-btn-view"
            onClick={() => navigate(`/workshops/${workshop._id}`)}
          >
            <FiEye size={14} />
            <span>View</span>
          </button>
        </div>
      )}

      {/* Joined Actions */}
      {type === "joined" && (
        <div className="mwp-card-footer">
          <span className="mwp-enrolled-badge">
            <FiCheckCircle size={14} />
            Enrolled
          </span>
          <button
            className="mwp-btn mwp-btn-view"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/workshops/${workshop._id}`);
            }}
          >
            <span>View Workshop</span>
            <FiChevronRight size={14} />
          </button>
        </div>
      )}
    </motion.article>
  );
};

// Application Card
const MWPApplicationCard = ({ application, formatDate }) => {
  const navigate = useNavigate();

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: "#f59e0b", icon: FiClock, text: "Pending Review" },
      approved: { color: "#10b981", icon: FiCheckCircle, text: "Approved" },
      rejected: { color: "#ef4444", icon: FiXCircle, text: "Rejected" },
      payment_pending: {
        color: "#3b82f6",
        icon: FiDollarSign,
        text: "Payment Pending",
      },
      payment_rejected: {
        color: "#f97316",
        icon: FiAlertCircle,
        text: "Payment Rejected",
      },
    };
    return configs[status] || { color: "#64748b", icon: FiClock, text: status };
  };

  const statusConfig = getStatusConfig(application.applicationStatus);
  const StatusIcon = statusConfig.icon;

  return (
    <motion.article className="mwp-application-card" variants={itemVariants}>
      {/* Header */}
      <div className="mwp-app-header">
        <div className="mwp-app-title-wrap">
          <h3 className="mwp-app-title">{application.workshopTitle}</h3>
          {application.workshopOrganizer?.name && (
            <p className="mwp-app-organizer">
              <FiUser size={12} />
              {application.workshopOrganizer.name}
            </p>
          )}
        </div>
        <span
          className="mwp-app-status"
          style={{ "--status-color": statusConfig.color }}
        >
          <StatusIcon size={14} />
          {statusConfig.text}
        </span>
      </div>

      {/* Info */}
      <div className="mwp-app-info">
        <div className="mwp-app-date">
          <FiCalendar size={14} />
          <span>Applied: {formatDate(application.appliedAt)}</span>
        </div>
      </div>

      {/* Rejection Details */}
      {(application.applicationStatus === "rejected" ||
        application.applicationStatus === "payment_rejected") && (
        <div className="mwp-rejection-box">
          <div className="mwp-rejection-header">
            <FiXCircle size={16} />
            <span>
              {application.applicationStatus === "payment_rejected"
                ? "Payment Rejected"
                : "Application Rejected"}
            </span>
          </div>
          {(application.rejectionReason ||
            application.paymentProof?.rejectionReason) && (
            <p className="mwp-rejection-reason">
              {application.rejectionReason ||
                application.paymentProof?.rejectionReason}
            </p>
          )}
          <button
            className="mwp-btn mwp-btn-outline"
            onClick={() => navigate(`/workshops/${application.workshopId}`)}
          >
            <FiEye size={14} />
            <span>View Workshop</span>
          </button>
        </div>
      )}

      {/* Pending Actions */}
      {(application.applicationStatus === "pending" ||
        application.applicationStatus === "payment_pending") && (
        <div className="mwp-pending-actions">
          <button
            className="mwp-btn mwp-btn-edit"
            onClick={() =>
              navigate(`/workshops/${application.workshopId}/edit-request`)
            }
          >
            <FiEdit3 size={14} />
            <span>Edit Request</span>
          </button>
          <button
            className="mwp-btn mwp-btn-outline"
            onClick={() => navigate(`/workshops/${application.workshopId}`)}
          >
            <FiEye size={14} />
            <span>View</span>
          </button>
        </div>
      )}

      {/* Approved */}
      {application.applicationStatus === "approved" && (
        <div className="mwp-approved-box">
          <div className="mwp-approved-header">
            <FiAward size={18} />
            <span>Congratulations! You're enrolled</span>
          </div>
          <button
            className="mwp-btn mwp-btn-primary"
            onClick={() => navigate(`/workshops/${application.workshopId}`)}
          >
            <FiBookOpen size={14} />
            <span>Go to Workshop</span>
          </button>
        </div>
      )}
    </motion.article>
  );
};

// Empty State
const MWPEmptyState = ({ type, requestFilter }) => {
  const navigate = useNavigate();

  const getContent = () => {
    if (type === "requests") {
      if (requestFilter === "all") {
        return {
          icon: FiInbox,
          title: "No applications yet",
          desc: "You haven't applied to any workshops. Start exploring!",
          action: { label: "Browse Workshops", path: "/workshops" },
        };
      }
      return {
        icon: FiInbox,
        title: `No ${requestFilter} applications`,
        desc: `You don't have any ${requestFilter} applications.`,
        action: null,
      };
    }

    const contents = {
      created: {
        icon: FiGrid,
        title: "No published workshops",
        desc: "You haven't published any workshops yet. Create one to get started!",
        action: { label: "Create Workshop", path: "/workshops/create" },
      },
      drafts: {
        icon: FiFileText,
        title: "No draft workshops",
        desc: "You don't have any workshops in progress.",
        action: { label: "Create Workshop", path: "/workshops/create" },
      },
      joined: {
        icon: FiUsers,
        title: "No joined workshops",
        desc: "You haven't joined any workshops yet. Explore and enroll!",
        action: { label: "Browse Workshops", path: "/workshops" },
      },
    };
    return contents[type] || contents.created;
  };

  const content = getContent();
  const Icon = content.icon;

  return (
    <motion.div className="mwp-empty" variants={itemVariants}>
      <div className="mwp-empty-icon">
        <Icon size={40} />
      </div>
      <h3 className="mwp-empty-title">{content.title}</h3>
      <p className="mwp-empty-desc">{content.desc}</p>
      {content.action && (
        <button
          className="mwp-btn mwp-btn-primary"
          onClick={() => navigate(content.action.path)}
        >
          <FiPlus size={16} />
          <span>{content.action.label}</span>
        </button>
      )}
    </motion.div>
  );
};

// Loading Skeleton
const MWPSkeleton = () => (
  <div className="mwp-skeleton-grid">
    {[1, 2, 3].map((i) => (
      <div key={i} className="mwp-skeleton-card">
        <div className="mwp-skeleton-header" />
        <div className="mwp-skeleton-title" />
        <div className="mwp-skeleton-desc" />
        <div className="mwp-skeleton-meta" />
      </div>
    ))}
  </div>
);

// ========================================
// MAIN COMPONENT
// ========================================
const MyWorkshops = ({ currentUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [workshops, setWorkshops] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    return ["created", "drafts", "joined", "requests"].includes(tabParam)
      ? tabParam
      : "created";
  });
  const [publishingWorkshop, setPublishingWorkshop] = useState(null);
  const [requestFilter, setRequestFilter] = useState("all");

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === "requests") {
      fetchApplicationHistory();
    } else {
      fetchMyWorkshops();
    }
  }, [activeTab, requestFilter]);

  const fetchMyWorkshops = async () => {
    try {
      setLoading(true);
      setError(null);
      let response;

      if (activeTab === "drafts") {
        response = await workshopApi.getMyWorkshops("organizer", "draft");
      } else if (activeTab === "created") {
        response = await workshopApi.getMyWorkshops("organizer", "published");
      } else if (activeTab === "joined") {
        response = await workshopApi.getMyWorkshops("participant", "all");
      }

      if (response?.success) {
        setWorkshops(response.data.workshops || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {};
      if (requestFilter !== "all" && requestFilter !== "rejected") {
        filterParams.status = requestFilter;
      }

      const result = await workshopApi.getMyApplicationHistory(filterParams);

      if (result?.success) {
        let filtered = result.data.applications || [];

        if (requestFilter === "rejected") {
          filtered = filtered.filter(
            (app) =>
              app.applicationStatus === "rejected" ||
              app.applicationStatus === "payment_rejected"
          );
        }

        setApplications(filtered);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handlePublishWorkshop = async (workshopId) => {
    try {
      setPublishingWorkshop(workshopId);

      const workshopResponse = await workshopApi.getWorkshopById(workshopId);
      if (!workshopResponse?.success) {
        throw new Error("Failed to load workshop details");
      }

      const workshop = workshopResponse.data.workshop;
      if (!workshop.schedule || workshop.schedule.length === 0) {
        setError(
          "This workshop needs a schedule before publishing. Please edit and add daily sessions."
        );
        return;
      }

      const response = await workshopApi.publishWorkshop(workshopId);
      if (response?.success) {
        setWorkshops((prev) => prev.filter((w) => w._id !== workshopId));
        alert("Workshop published successfully!");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishingWorkshop(null);
    }
  };

  const handleDeleteWorkshop = async (workshopId) => {
    if (!window.confirm("Are you sure you want to delete this workshop?")) {
      return;
    }

    try {
      const response = await workshopApi.deleteWorkshop(workshopId);
      if (response?.success) {
        setWorkshops((prev) => prev.filter((w) => w._id !== workshopId));
        alert("Workshop deleted successfully!");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditWorkshop = (workshopId) => {
    navigate(`/workshops/${workshopId}/edit`);
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      const dateValue = dateString.$date || dateString;
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "Invalid date";
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  }, []);

  // Filter counts for requests tab
  const filterCounts = useMemo(() => {
    if (activeTab !== "requests") return null;
    return {
      all: applications.length,
      pending: applications.filter((a) => a.applicationStatus === "pending")
        .length,
      approved: applications.filter((a) => a.applicationStatus === "approved")
        .length,
      rejected: applications.filter(
        (a) =>
          a.applicationStatus === "rejected" ||
          a.applicationStatus === "payment_rejected"
      ).length,
      payment_pending: applications.filter(
        (a) => a.applicationStatus === "payment_pending"
      ).length,
    };
  }, [activeTab, applications]);

  // Tab counts
  const tabCounts = useMemo(() => {
    // We'd need to fetch all counts, for now just show workshops length
    return {
      created: activeTab === "created" ? workshops.length : undefined,
      drafts: activeTab === "drafts" ? workshops.length : undefined,
      joined: activeTab === "joined" ? workshops.length : undefined,
      requests: activeTab === "requests" ? applications.length : undefined,
    };
  }, [activeTab, workshops, applications]);

  return (
    <div className="mwp-page">
      <div className="mwp-container">
        {/* Header */}
        <motion.header
          className="mwp-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mwp-header-left">
            <h1 className="mwp-title">My Workshops</h1>
            <p className="mwp-subtitle">
              Manage your workshops and applications
            </p>
          </div>
          <button
            className="mwp-btn mwp-btn-primary mwp-create-btn"
            onClick={() => navigate("/workshops/create")}
          >
            <FiPlus size={18} />
            <span>Create Workshop</span>
          </button>
        </motion.header>

        {/* Tabs */}
        <motion.nav
          className="mwp-tabs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <MWPTabButton
            icon={FiGrid}
            label="Published"
            count={tabCounts.created}
            isActive={activeTab === "created"}
            onClick={() => setActiveTab("created")}
          />
          <MWPTabButton
            icon={FiFileText}
            label="Drafts"
            count={tabCounts.drafts}
            isActive={activeTab === "drafts"}
            onClick={() => setActiveTab("drafts")}
          />
          <MWPTabButton
            icon={FiUsers}
            label="Joined"
            count={tabCounts.joined}
            isActive={activeTab === "joined"}
            onClick={() => setActiveTab("joined")}
          />
          <MWPTabButton
            icon={FiInbox}
            label="My Requests"
            count={tabCounts.requests}
            isActive={activeTab === "requests"}
            onClick={() => setActiveTab("requests")}
          />
        </motion.nav>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              className="mwp-error"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FiAlertCircle size={18} />
              <p>{error}</p>
              <button onClick={() => setError(null)}>
                <FiXCircle size={18} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Request Filters */}
        {activeTab === "requests" && (
          <motion.div
            className="mwp-filters"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            {[
              { key: "all", label: "All", color: "#64748b" },
              { key: "pending", label: "Pending", color: "#f59e0b" },
              { key: "approved", label: "Approved", color: "#10b981" },
              { key: "rejected", label: "Rejected", color: "#ef4444" },
              {
                key: "payment_pending",
                label: "Payment Pending",
                color: "#3b82f6",
              },
            ].map((filter) => (
              <MWPFilterPill
                key={filter.key}
                label={filter.label}
                count={filterCounts?.[filter.key]}
                isActive={requestFilter === filter.key}
                onClick={() => setRequestFilter(filter.key)}
                color={filter.color}
              />
            ))}
          </motion.div>
        )}

        {/* Content */}
        <main className="mwp-content">
          {loading ? (
            <MWPSkeleton />
          ) : activeTab === "requests" ? (
            applications.length > 0 ? (
              <motion.div
                className="mwp-applications-list"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {applications.map((app, index) => (
                  <MWPApplicationCard
                    key={`${app.workshopId}-${app.appliedAt}-${index}`}
                    application={app}
                    formatDate={formatDate}
                  />
                ))}
              </motion.div>
            ) : (
              <MWPEmptyState type="requests" requestFilter={requestFilter} />
            )
          ) : workshops.length > 0 ? (
            <motion.div
              className="mwp-workshops-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {workshops.map((workshop) => (
                <MWPWorkshopCard
                  key={workshop._id}
                  workshop={workshop}
                  type={activeTab}
                  onEdit={handleEditWorkshop}
                  onPublish={handlePublishWorkshop}
                  onDelete={handleDeleteWorkshop}
                  publishing={publishingWorkshop}
                />
              ))}
            </motion.div>
          ) : (
            <MWPEmptyState type={activeTab} />
          )}
        </main>
      </div>
    </div>
  );
};

export default MyWorkshops;
